import { CartAddEvent } from '@theme/events';

/** Product data is server-rendered in the current Shopify market; no hard-coded IDs or prices. */
export class WaterGlassProduct extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.data = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.form = this.querySelector('.dg-purchase-form');
    this.quantity = this.querySelector('[data-quantity]');
    this.variantSelect = this.querySelector('[data-variant-select]');
    this.planSelect = this.querySelector('[data-plan-select]');
    this.variant = this.data.variants.find(v => String(v.id) === String(this.variantSelect?.value || this.data.selected));
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'EUR' });
    this.dataset.enhanced = 'true';
    this.addEventListener('click', event => this.onClick(event), options);
    this.addEventListener('change', event => this.onChange(event), options);
    this.quantity?.addEventListener('input', () => this.updateTotals(), options);
    this.form?.addEventListener('submit', event => this.submit(event), options);
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-media-id]')];
    this.track?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.syncGallery());
    }, { ...options, passive: true });
    this.track?.addEventListener('keydown', event => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.moveGallery(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    this.dialog = this.querySelector('[data-lightbox]');
    this.dialog?.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, options);
    this.dialog?.addEventListener('close', () => this.zoomTrigger?.focus(), options);
    this.observeSticky();
    this.updateVariant(false);
    document.addEventListener('shopify:block:select', event => {
      const block = event.target.closest('.dg-block');
      if (block && this.contains(block)) block.querySelector('details')?.setAttribute('open', '');
    }, options);
  }

  onClick(event) {
    const quantityButton = event.target.closest('[data-quantity-step]');
    if (quantityButton && this.quantity) {
      Number(quantityButton.dataset.quantityStep) > 0 ? this.quantity.stepUp() : this.quantity.stepDown();
      this.updateTotals();
    }
    const thumb = event.target.closest('[data-thumb]');
    if (thumb) this.showMedia(thumb.dataset.thumb);
    const arrow = event.target.closest('[data-gallery-step]');
    if (arrow) this.moveGallery(Number(arrow.dataset.galleryStep));
    const zoom = event.target.closest('[data-zoom]');
    if (zoom && this.dialog?.showModal) {
      const img = this.dialog.querySelector('[data-zoom-image]');
      img.src = zoom.dataset.zoom;
      img.alt = zoom.dataset.alt;
      this.zoomTrigger = zoom;
      this.dialog.showModal();
    }
    if (event.target.closest('[data-close-zoom]')) this.dialog?.close();
  }

  onChange(event) {
    if (event.target === this.variantSelect) {
      this.variant = this.data.variants.find(v => String(v.id) === this.variantSelect.value);
      this.updateVariant(true);
    }
    if (event.target.matches('[data-addon-variant]')) {
      const option = event.target.selectedOptions[0];
      const checkbox = event.target.closest('[data-addon]').querySelector('[data-addon-check]');
      Object.assign(checkbox.dataset, { variant: option.value, price: option.dataset.price, addonQuantity: option.dataset.addonQuantity });
      event.target.closest('[data-addon]').querySelector('[data-addon-price]').textContent = '+ ' + this.format(Number(option.dataset.price) * Number(option.dataset.addonQuantity));
    }
    if (event.target === this.quantity && !this.quantity.checkValidity()) {
      this.quantity.value = this.variant.min;
    }
    this.updateTotals();
  }

  updateVariant(updateURL) {
    const variant = this.variant;
    if (!variant) return;
    if (this.quantity) {
      this.quantity.min = variant.min;
      this.quantity.step = variant.step;
      if (variant.max != null) this.quantity.max = variant.max;
      else this.quantity.removeAttribute('max');
      if (!this.quantity.checkValidity()) this.quantity.value = variant.min;
    }
    if (this.planSelect) {
      const previousPlan = this.planSelect.value;
      const oneTime = this.planSelect.querySelector('option[value=""]')?.cloneNode(true);
      this.planSelect.replaceChildren();
      if (oneTime) this.planSelect.append(oneTime);
      for (const plan of variant.plans) this.planSelect.add(new Option(plan.name, String(plan.id)));
      if ([...this.planSelect.options].some(p => p.value === previousPlan)) this.planSelect.value = previousPlan;
    }
    const compare = this.querySelector('[data-compare]');
    if (compare) {
      compare.textContent = this.plain(variant.compareText);
      compare.hidden = !(variant.compare > variant.price);
    }
    if (updateURL) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
    if (variant.media) this.showMedia(variant.media, false);
    this.updateTotals();
  }

  plain(value) {
    const template = document.createElement('template');
    template.innerHTML = value || '';
    return template.content.textContent;
  }

  format(cents) {
    // Derive separators and currency placement from Shopify's current-market money output.
    const sample = this.plain(this.data.moneySample);
    const numeric = sample.match(/\d[\d\s.,’']*\d/);
    if (!numeric) return this.money.format(cents / 100);
    const decimal = numeric[0].match(/([.,])\d{2}$/)?.[1];
    const integerSample = decimal ? numeric[0].slice(0, -3) : numeric[0];
    const separator = integerSample.match(/[^\d]/)?.[0] || '';
    const [whole, fraction] = (cents / 100).toFixed(decimal ? 2 : 0).split('.');
    const grouped = separator ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : whole;
    return sample.replace(numeric[0], grouped + (decimal ? decimal + fraction : ''));
  }

  updateTotals() {
    if (!this.variant) return;
    const plan = this.variant.plans.find(p => String(p.id) === this.planSelect?.value);
    const price = plan?.price ?? this.variant.price;
    const quantity = Math.max(this.variant.min, Number(this.quantity?.value) || this.variant.min);
    const extras = [...this.querySelectorAll('[data-addon-check]:checked')].reduce((sum, input) => sum + Number(input.dataset.price) * Number(input.dataset.addonQuantity), 0);
    this.querySelectorAll('[data-price]').forEach(n => { if (!n.matches('input,option')) n.textContent = this.plain(plan?.priceText || this.variant.priceText); });
    const unit = this.querySelector('[data-unit-price]');
    if (unit) unit.textContent = this.plain(plan?.unitText || this.variant.unitText);
    this.querySelectorAll('[data-total]').forEach(n => { n.textContent = this.format(price * quantity + extras); });
    const available = this.variant.available && (!this.data.requiresPlan || !!plan);
    this.querySelectorAll('[data-submit]').forEach(button => {
      button.disabled = this.busy || !available;
      const label = this.busy ? this.dataset.adding : available ? button.dataset.label : this.dataset.soldOut;
      const target = button.querySelector('[data-submit-label]');
      if (target) target.textContent = label;
      else button.textContent = label;
    });
    this.querySelectorAll('[data-quantity-step]').forEach(button => {
      button.disabled = Number(button.dataset.quantityStep) < 0 ? quantity <= this.variant.min : this.variant.max != null && quantity >= this.variant.max;
    });
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy || !this.variant?.available || !this.form.reportValidity()) return;
    if (this.data.requiresPlan && !this.planSelect?.value) return;
    this.busy = true;
    this.form.setAttribute('aria-busy', 'true');
    const feedback = this.querySelector('[data-feedback]');
    feedback.hidden = true;
    this.updateTotals();
    const main = { id: this.variant.id, quantity: Number(this.quantity.value) };
    if (this.planSelect?.value) main.selling_plan = Number(this.planSelect.value);
    const items = [main, ...[...this.querySelectorAll('[data-addon-check]:checked')].map(input => ({ id: Number(input.dataset.variant), quantity: Number(input.dataset.addonQuantity) }))];
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(n => n.dataset.sectionId))].slice(0, 5);
    try {
      const endpoint = this.dataset.cartAddUrl.replace(/\.js$/, '') + '.js';
      const response = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        signal: this.controller.signal,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : this.dataset.error);
      // Native cart-icon treats this source as an increment, not an absolute cart count.
      this.dispatchEvent(new CartAddEvent({}, this.dataset.productId, { source: 'product-form-component', productId: this.dataset.productId, itemCount: items.reduce((sum, item) => sum + item.quantity, 0), sections: data.sections }));
      feedback.textContent = this.dataset.added;
      feedback.hidden = false;
      if (!document.querySelector('cart-drawer-component')) window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      feedback.textContent = error instanceof TypeError || error instanceof SyntaxError ? this.dataset.error : error.message || this.dataset.error;
      feedback.hidden = false;
      feedback.focus();
    } finally {
      this.busy = false;
      this.form.removeAttribute('aria-busy');
      if (this.isConnected) this.updateTotals();
    }
  }

  showMedia(id, smooth = true) {
    const slide = this.slides.find(n => n.dataset.mediaId === String(id));
    if (!slide || !this.track) return;
    const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    this.track.scrollTo({ left, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
    this.setCurrentMedia(slide);
  }

  setCurrentMedia(slide) {
    this.querySelectorAll('[data-thumb]').forEach(button => button.setAttribute('aria-current', String(button.dataset.thumb === slide.dataset.mediaId)));
    this.slides.filter(n => n !== slide).forEach(n => n.querySelectorAll('video').forEach(video => video.pause()));
  }

  syncGallery() {
    if (!this.slides.length) return;
    const edge = this.track.getBoundingClientRect().left;
    const closest = this.slides.reduce((a, b) => Math.abs(a.getBoundingClientRect().left - edge) < Math.abs(b.getBoundingClientRect().left - edge) ? a : b);
    this.setCurrentMedia(closest);
  }

  moveGallery(step) {
    const currentId = this.querySelector('[data-thumb][aria-current="true"]')?.dataset.thumb;
    const index = Math.max(0, this.slides.findIndex(n => n.dataset.mediaId === currentId));
    const next = this.slides[(index + step + this.slides.length) % this.slides.length];
    if (next) this.showMedia(next.dataset.mediaId);
  }

  observeSticky() {
    const sticky = this.querySelector('[data-sticky]');
    const purchase = this.querySelector('[data-purchase]');
    if (!sticky || !purchase || !('IntersectionObserver' in window)) return;
    this.stickyObserver = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }, { threshold: 0 });
    this.stickyObserver.observe(purchase);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.stickyObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
    this.dialog?.close();
    delete this.dataset.enhanced;
  }
}

if (!customElements.get('water-glass-product')) customElements.define('water-glass-product', WaterGlassProduct);

if (!customElements.get('glass-related')) customElements.define('glass-related', class extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.controller) return;
    this.controller = new AbortController();
    const load = async () => {
      this.observer?.disconnect();
      try {
        const response = await fetch(this.dataset.url, { signal: this.controller.signal });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector('[data-related-content]');
        if (content?.querySelector('.dg-product-card')) this.querySelector('[data-related-content]').replaceWith(content);
      } catch { /* Keep the server-rendered product picker fallback. */ }
    };
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) load(); }, { rootMargin: '400px' });
      this.observer.observe(this);
    } else load();
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; this.observer?.disconnect(); }
});
